import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import AnimateOnScroll from './AnimateOnScroll.js';
import PageHero from './PageHero.js';
import regionLowermainland from '../assets/region-lowermainland.jpg';

const LowerMainlandPage = () => {
  return (
    <div className="bg-custom-earth">
      <div className="wood-container">
        <div className="min-h-screen flex flex-col">
          <Helmet>
            <title>Vegetation Management Vancouver & Lower Mainland | Boultbee</title>
            <meta name="description" content="Industrial herbicide application in the Lower Mainland and Fraser Valley. Bare ground control, rail corridor spraying, and noxious weed management." />
            <link rel="canonical" href="https://www.weedmanager.com/service-areas/lower-mainland" />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content="Boultbee Vegetation Management" />
            <meta property="og:title" content="Vegetation Management Vancouver & Lower Mainland | Boultbee" />
            <meta property="og:description" content="Industrial herbicide application in the Lower Mainland and Fraser Valley. Bare ground control, rail corridor spraying, and noxious weed management." />
            <meta property="og:url" content="https://www.weedmanager.com/service-areas/lower-mainland" />
            <meta property="og:image" content="https://www.weedmanager.com/images/og-lowermainland.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Vegetation Management Vancouver & Lower Mainland | Boultbee" />
            <meta name="twitter:description" content="Industrial herbicide application in the Lower Mainland and Fraser Valley. Bare ground control, rail corridor spraying, and noxious weed management." />
            <meta name="twitter:image" content="https://www.weedmanager.com/images/og-lowermainland.jpg" />
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.weedmanager.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Lower Mainland & Fraser Valley", "item": "https://www.weedmanager.com/service-areas/lower-mainland" }
                ]
              })}
            </script>
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Service",
                "serviceType": "Industrial Vegetation Management",
                "provider": {
                  "@type": "LocalBusiness",
                  "name": "Boultbee Vegetation Management",
                  "url": "https://www.weedmanager.com"
                },
                "areaServed": [
                  { "@type": "City", "name": "Vancouver" },
                  { "@type": "City", "name": "Burnaby" },
                  { "@type": "City", "name": "Surrey" },
                  { "@type": "City", "name": "Richmond" },
                  { "@type": "City", "name": "New Westminster" },
                  { "@type": "City", "name": "Coquitlam" },
                  { "@type": "City", "name": "Delta" },
                  { "@type": "City", "name": "Langley" },
                  { "@type": "City", "name": "Abbotsford" },
                  { "@type": "City", "name": "Chilliwack" }
                ],
                "description": "Industrial herbicide application services in the Lower Mainland and Fraser Valley including bare ground control, rail corridor spraying, and noxious weed management."
              })}
            </script>
          </Helmet>

          <PageHero
            title="Lower Mainland & Fraser Valley"
            subtitle="Industrial herbicide application for the Lower Mainland and Fraser Valley"
            backgroundImage={regionLowermainland}
          />

          <AnimateOnScroll animation="fade-in-up">
            <p className="text-center text-lg md:text-xl font-serif italic text-green-900 max-w-3xl mx-auto px-6 py-6">Boultbee serves industrial clients across the Lower Mainland and Fraser Valley, bringing 40+ years of vegetation management expertise from our Okanagan base.</p>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Industrial Vegetation Management</h2>
              <p className="text-gray-700">Boultbee provides bare ground control and industrial vegetation management for clients throughout the Lower Mainland and Fraser Valley. We bring the same equipment and expertise that our industrial clients rely on across the rest of British Columbia. We service industrial parks and infrastructure sites from Surrey and Langley through to Abbotsford and Chilliwack.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Fraser Valley & Eastern Lower Mainland</h2>
              <p className="text-gray-700">The Fraser Valley's mix of agricultural, industrial, and transportation infrastructure creates steady demand for professional vegetation management. Boultbee serves industrial properties from Langley and Abbotsford through to Chilliwack and Hope. The region's mild, wet climate drives aggressive year-round weed growth — our seasonal programs keep industrial sites under control.</p>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Expanding Our Lower Mainland Presence</h2>
              <p className="text-gray-700">Based in Penticton with over four decades of experience across BC, Boultbee is actively growing our Lower Mainland service offering. We bring specialized equipment — including hi-rail spray vehicles and continuous agitation systems — that most local contractors don't have. If you manage industrial vegetation in the Lower Mainland or Fraser Valley, we'd like to hear from you.</p>
            </div>
          </AnimateOnScroll>

          {/* Other Service Areas */}
          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">Other Regions We Serve</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link to="/service-areas/okanagan" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Okanagan</h3>
                  <p className="text-gray-700 text-sm mt-1">Penticton, Kelowna & Vernon</p>
                </Link>
                <Link to="/service-areas/interior-bc" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Interior BC</h3>
                  <p className="text-gray-700 text-sm mt-1">Kamloops, Merritt & Revelstoke</p>
                </Link>
                <Link to="/service-areas/northern-bc" className="block bg-white/80 rounded-lg p-4 text-center hover-lift border border-gray-200">
                  <svg className="w-6 h-6 text-green-700 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" /></svg>
                  <h3 className="font-bold text-gray-900">Northern BC</h3>
                  <p className="text-gray-700 text-sm mt-1">Prince George & Central Interior</p>
                </Link>
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-in-up">
            <div className="text-container-blur text-center">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Get a Quote</h2>
              <p className="text-gray-700 mb-5">Industrial vegetation management with 40+ years of BC experience. <Link to="/services" className="text-green-700 underline hover:text-green-900">View all services</Link> or contact us for a site assessment.</p>
              <Link to="/contact" className="cta-button">
                Get a Quote
              </Link>
            </div>
          </AnimateOnScroll>
        </div>
      </div>
    </div>
  );
};

export default LowerMainlandPage;
